import '../../constants/const.css';
import React, { useEffect, useRef } from 'react';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ChartOptions, ChartDataset } from 'chart.js';
import { Dayjs } from '../../logic/classDayjs';
import { FORMAT_DATE_M, FORMAT_DATE_D } from '../../constants/const';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

interface pitProps {
  dayCount: number[];
  directionY: String;
  dayStr: string[];
  monthIndex: number;
}

interface dayProp {
  month: number;
  day: number;
  view: string;
}

const COLOR_LINE_WIDTH: number = 2;
const GRID_LINE_WIDTH: number = 0;
const months: number[] = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
const WEEK_DAYS = ['日', '月', '火', '水', '木', '金', '土'];

const BurnGraph3: React.FC<pitProps> = (pitProps) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      if (ctx) {
        // 既存のチャートインスタンスが存在する場合、破棄する
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }

        const dayProps: dayProp[] = [];

        for (let i = 0; i < pitProps.dayStr.length; i++) {
          const date = new Date(pitProps.dayStr[i]);
          const retDay: dayProp = {
            month: Number(Dayjs.convertStr(date, FORMAT_DATE_M)),
            day: Number(Dayjs.convertStr(date, FORMAT_DATE_D)),
            view: `${date.getMonth() + 1}/${date.getDate()}(${WEEK_DAYS[date.getDay()]})`,
          };
          if (pitProps.monthIndex === 0) {
            dayProps.push(retDay);
          } else if (months[pitProps.monthIndex - 1] === retDay.month) {
            dayProps.push(retDay);
          }
        }
        const labelStr: string[] = [];

        let count = 0;
        if (pitProps.monthIndex === 0) {
          for (let i = 0; i < pitProps.dayCount.length; i++) {
            for (let j = 0; j < pitProps.dayCount[i]; j++) {
              if (dayProps.length > count) {
                if (dayProps[count].month === months[i] && dayProps[count].day === j + 1) {
                  labelStr.push((pitProps.directionY === 'Top' ? '↓' : '↑').concat(dayProps[count].view));
                  count = count + 1;
                } else {
                  labelStr.push('');
                }
              } else {
                labelStr.push('');
              }
            }
          }
        } else {
          for (let j = 0; j < pitProps.dayCount[pitProps.monthIndex - 1]; j++) {
            if (dayProps.length > count) {
              if (dayProps[count].day === j + 1) {
                labelStr.push((pitProps.directionY === 'Top' ? '↓' : '↑').concat(dayProps[count].view));
                count = count + 1;
              } else {
                labelStr.push('');
              }
            } else {
              labelStr.push('');
            }
          }
        }

        const tempDatasets: ChartDataset<'line'>[] = [];
        tempDatasets.push({
          data:
            pitProps.monthIndex === 0
              ? pitProps.dayCount.length !== 0
                ? [
                    ...new Array(
                      pitProps.dayCount.reduce(function (a, x) {
                        return a + x;
                      }),
                    ).fill(1),
                  ]
                : [...new Array(366).fill(1)]
              : [...new Array(pitProps.dayCount[pitProps.monthIndex - 1]).fill(1)],
          borderColor: 'white',
          borderWidth: COLOR_LINE_WIDTH,
          tension: 0,
          pointRadius: 0,
          fill: false,
        });

        const data = { labels: labelStr, datasets: tempDatasets };

        const options: ChartOptions<'line'> = {
          responsive: true,
          plugins: {
            legend: {
              display: false,
              position: 'top',
            },
            title: {
              display: false,
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: 14,
                },
                autoSkip: false,
                labelOffset: 14,
                callback: (value: string | number, index: number) => {
                  const numericValue = typeof value === 'number' ? value : parseFloat(value);
                  const label = data.labels[numericValue];
                  return label !== '' ? label : '';
                },
              },
              grid: {
                color: 'black',
                drawTicks: false,
                lineWidth: GRID_LINE_WIDTH,
              },
            },
            y: {
              beginAtZero: true,
              ticks: {
                display: false,
              },
              grid: {
                color: 'white',
                drawTicks: false,
                lineWidth: GRID_LINE_WIDTH,
              },
            },
          },
        };
        chartInstanceRef.current = new Chart(ctx, {
          type: 'line',
          data: data,
          options: options,
        });
      }
    }

    // クリーンアップ時にチャートを破棄
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [pitProps.dayCount, pitProps.directionY, pitProps.dayStr, pitProps.monthIndex]);

  return (
    <div
      style={{
        height: '40px',
        width: '875px',
        minWidth: '875px',
        maxHeight: '40px',
        transform: pitProps.directionY === 'Top' ? 'translateY(-50%) translateX(6px)' : 'translateY(-50%) translateX(6px)',
      }}
    >
      <canvas
        ref={chartRef}
        style={{
          height: '40px',
          width: '875px',
          maxHeight: '40px',
          minWidth: '875px',
        }}
      />
    </div>
  );
};

export default BurnGraph3;
